import React from 'react';
import { Card, CardContent, Box, Text, Flex, Button, Heading, Markdown, DangerSection, DangerAction, LoadingOrbit, TagBadge } from '@chatsdet/ui';
import { Trash, Copy, Check, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import { useSandboxArtifacts, type SandboxArtifact } from '../../../hooks/useSandboxArtifacts';

function artifactLanguage(artifactType: string, filename: string): string {
  const byType: Record<string, string> = {
    'docker-compose': 'yaml',
    'dockerfile': 'dockerfile',
    'startup-script': 'bash',
    'setup-script': 'bash',
    'seed-script': 'bash',
    'environment-config': 'bash',
  };
  if (byType[artifactType]) return byType[artifactType];

  const ext = filename.split('.').pop()?.toLowerCase();
  const byExt: Record<string, string> = {
    yml: 'yaml', yaml: 'yaml', json: 'json', sh: 'bash',
    ts: 'typescript', js: 'javascript', py: 'python',
    sql: 'sql', toml: 'toml', env: 'bash',
  };
  return byExt[ext ?? ''] ?? 'text';
}

function artifactLabel(artifactType: string): string {
  const labels: Record<string, string> = {
    'docker-compose': 'Docker Compose',
    'dockerfile': 'Dockerfile',
    'startup-script': 'Startup Script',
    'setup-script': 'Setup Script',
    'seed-script': 'Seed Script',
    'environment-config': 'Environment Config',
    'action': 'Action',
  };
  return labels[artifactType] ?? artifactType;
}

function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="sm" onPress={handleCopy}>
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
}

function ArtifactCard({
  artifact,
  isOpen,
  onToggle,
}: {
  artifact: SandboxArtifact;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const language = artifactLanguage(artifact.artifactType, artifact.filename);

  return (
    <Card padding="none">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 border-b border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <Flex align="center" gap="sm">
          {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <Text size="sm" weight="medium" className="font-mono">
            {artifact.filename}
          </Text>
          <TagBadge name={artifactLabel(artifact.artifactType)} color="gray" hideDot />
        </Flex>
        {isOpen && (
          <div onClick={(e) => e.stopPropagation()}>
            <CopyButton content={artifact.content} />
          </div>
        )}
      </button>
      {isOpen && (
        <Box className="max-h-[400px] overflow-auto bg-slate-950">
          <SyntaxHighlighter
            language={language}
            style={vscDarkPlus}
            customStyle={{ margin: 0, background: 'transparent', padding: '1rem' }}
            wrapLongLines
          >
            {artifact.content}
          </SyntaxHighlighter>
        </Box>
      )}
    </Card>
  );
}

/** Fallback CodeBlock for legacy inline template fields */
function CodeBlock({ title, content, language }: { title: string; content: string; language: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card padding="none">
      <Flex
        align="center"
        justify="between"
        className="px-4 py-3 border-b border-border bg-muted/30"
      >
        <Text size="sm" weight="medium" className="font-mono">
          {title}
        </Text>
        <Button variant="ghost" size="sm" onPress={handleCopy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </Flex>
      <Box className="max-h-[400px] overflow-auto bg-slate-950">
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{ margin: 0, background: 'transparent', padding: '1rem' }}
          wrapLongLines
        >
          {content}
        </SyntaxHighlighter>
      </Box>
    </Card>
  );
}

interface TemplateTabProps {
  template: EnvironmentTemplate;
  templateId: string;
  onDelete: () => void;
  isDeleting: boolean;
}

export const TemplateTab: React.FC<TemplateTabProps> = ({
  template,
  templateId,
  onDelete,
  isDeleting,
}) => {
  const artifactsQuery = useSandboxArtifacts(templateId);
  const artifacts = artifactsQuery.data ?? [];
  const [openIds, setOpenIds] = React.useState<Set<string>>(new Set());

  const toggleArtifact = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasArtifacts = artifacts.length > 0;
  const hasLegacyContent = !!(template.dockerComposeContent || template.dockerfileContent);

  return (
    <div className="space-y-6">
      {/* Summary */}
      {template.analysisSummary && (
        <Card padding="md">
          <CardContent>
            <Heading level={4} className="mb-3">
              Summary
            </Heading>
            <Markdown className="text-sm">{template.analysisSummary}</Markdown>
          </CardContent>
        </Card>
      )}

      {/* Artifacts from API */}
      {artifactsQuery.isLoading ? (
        <Flex justify="center" className="py-8">
          <LoadingOrbit />
        </Flex>
      ) : hasArtifacts ? (
        <div className="space-y-3">
          <Flex align="center" gap="sm">
            <Heading level={4}>Artifacts</Heading>
            <TagBadge name={artifacts.length} color="gray" hideDot />
          </Flex>
          {artifacts.map((artifact) => (
            <ArtifactCard
              key={artifact.id}
              artifact={artifact}
              isOpen={openIds.has(artifact.id)}
              onToggle={() => toggleArtifact(artifact.id)}
            />
          ))}
        </div>
      ) : hasLegacyContent ? (
        /* Fallback for legacy templates without artifacts table entries */
        <div className="space-y-3">
          <Heading level={4}>Artifacts</Heading>
          {template.dockerComposeContent && (
            <CodeBlock title="docker-compose.yml" content={template.dockerComposeContent} language="yaml" />
          )}
          {template.dockerfileContent && (
            <CodeBlock
              title={template.dockerfilePath || 'Dockerfile'}
              content={template.dockerfileContent}
              language="dockerfile"
            />
          )}
        </div>
      ) : null}

      {/* Verification Logs */}
      {template.verificationLogs && (
        <Card padding="md">
          <CardContent>
            <Heading level={4} className="mb-3">
              Verification Logs
            </Heading>
            <Box className="rounded-lg border border-border bg-muted/30">
              <pre className="p-4 text-sm font-mono overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap">
                {template.verificationLogs}
              </pre>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Danger Zone */}
      <DangerSection>
        <DangerAction
          title="Delete Template"
          description="Permanently delete this environment template. This action cannot be undone."
        >
          <Button
            variant="destructive"
            size="sm"
            onPress={onDelete}
            isDisabled={isDeleting}
            iconLeft={<Trash size={16} />}
          >
            Delete Template
          </Button>
        </DangerAction>
      </DangerSection>
    </div>
  );
};
